import { registerRivals } from "./market";
import { rivals as rivalContent } from "@bd/content";
import { furnishRoom } from "./layout";
import { computeFinale } from "./goals";
import { compactState } from "./compact";
import { resetMarketCache } from "./market";
import { resetSalesCaches } from "./sales";
import { resetLeagueMemo } from "./goals";
import { resetUnlockCaches } from "./unlocks";
import { resetPerkCache } from "./perks";
import { resetFurnitureCache } from "./studio";

/** Remembered figures are worked out again from the world as it is (see resetSalesCaches). */
export function resetCaches(): void {
  resetSalesCaches();
  resetMarketCache();
  // every remembered figure, so a long-running server and a fresh one (or a reloaded save) always agree
  resetLeagueMemo();
  resetUnlockCaches();
  resetPerkCache();
  resetFurnitureCache();
}
import { dateOf, isQuarterStart, isYearStart, SEASON_WEEKS, weekOf } from "./calendar";
import { hashString } from "./rng";
import { createCompany } from "./company";
import { SYSTEMS } from "./systems";
import { log } from "./util";
import type { GameState } from "./state";
import { ngPlus } from "@bd/content";
import { eraFor } from "./ngplus";

export const STATE_VERSION = 4;

export interface PlayerSetup {
  id: string; // stable player key: "anton", "bot:greedy:2"
  company: string; // company name
  color?: string;
  notebook?: Record<string, number>; // New Game+: what the player learned last season
}

export interface NewGameOptions {
  seed: string;
  players: PlayerSetup[];
  mode?: "solo" | "season";
  newGamePlus?: boolean;
}

export function createGame(opts: NewGameOptions): GameState {
  const state: GameState = {
    version: STATE_VERSION,
    seed: opts.seed,
    week: 0,
    rng: { __seed: hashString(opts.seed) },
    nextId: 0,
    settings: {
      mode: opts.mode ?? (opts.players.length > 1 ? "season" : "solo"),
      safeUntilWeek: 240, // 2 hours of play at normal speed
      startCash: 8000,
      endWeek: SEASON_WEEKS,
      newGamePlus: opts.newGamePlus || undefined,
    },
    companies: [],
    rivals: rivalContent.map((r) => ({
      id: r.id, cash: r.cash, reputation: 50, nextRelease: r.from ? weekOf(r.from) : 4 + (hashString(r.id) % r.cadence),
      released: 0, bust: false, staffPoached: 0, copyQueue: [],
    })),
    market: { trends: [], games: [], archived: 0, news: [], shortage: null, themeCount: {}, comboCount: {}, awards: [] },
    log: [],
    finished: false,
  };
  for (const p of opts.players) {
    const co = createCompany(state, p);
    if (opts.newGamePlus) {
      co.flags.push("ngplus");
      co.research += ngPlus.startResearch;
      Object.assign(co.notebook, p.notebook ?? {});
    }
  }
  for (const s of SYSTEMS) s.init?.(state);
  log(state, "season", `Season begins: ${dateOf(0).label}. ${state.companies.length} companies, ${state.rivals.length} rival publishers.`);
  return state;
}

/**
 * Bring a saved game up to the current state shape. v1 (M1) saves lack the director, live-product and
 * achievement fields; they get empty defaults so an old save keeps playing.
 */
export function migrate(state: GameState): GameState {
  registerRivals(state);
  if ((state.version ?? 1) >= STATE_VERSION) return state;
  // v3: rooms come furnished and everyone needs a desk, so older rooms get their furniture now
  if ((state.version ?? 1) < 3) for (const co of state.companies) for (const room of co.rooms) furnishRoom(state, room);
  state.market.awards ??= [];
  for (const co of state.companies) {
    co.mods ??= [];
    co.flags ??= [];
    co.achievements ??= [];
    co.arcs ??= [];
    co.nextHype ??= 0;
    co.creditBonus ??= 0;
    co.awardBoost ??= 0;
    co.licenceOffers ??= [];
    co.stats.eventsAnswered ??= 0;
    co.stats.arcsDone ??= 0;
    co.director ??= { nextWeek: state.week + 8, nextArcWeek: state.week + 104, fired: {}, tension: "calm", lastEventWeek: state.week, startUnlocks: co.unlocked.length, logCursor: state.log.length };
    // version 2 saves: a story step waiting for an answer was saved as due: null (JSON has no Infinity) and came back every week
    for (const run of co.arcs) if (run.due === null || !Number.isFinite(run.due)) run.due = 1e9;
  }
  state.version = STATE_VERSION;
  return state;
}

/** Advance the world by one week. Pure function of the state (mutates it in place). */
export function step(state: GameState): void {
  if (state.finished) return;
  resetCaches();
  if (isYearStart(state.week) && state.week > 0) compactState(state);
  const d = dateOf(state.week);
  if (isYearStart(state.week) && d.year > 1965 && d.era.from === d.year) {
    const era = eraFor(state, state.week);
    log(state, "era", `A new era begins: ${era.name} (${era.from}–${era.to}). ${era.blurb}`);
  }
  for (const s of SYSTEMS) s.week(state);
  // the log keeps every milestone; everyday lines older than the last few thousand go
  if (state.log.length > 6000) {
    const keep = new Set(["move", "acquire", "store", "direction", "award", "release", "bankrupt", "era", "finale", "misprint", "shipping", "company", "season"]);
    const cut = state.log.length - 3000;
    state.log = state.log.filter((e, i) => i >= cut || keep.has(e.kind));
  }
  state.week += 1;
  if (isQuarterStart(state.week)) log(state, "quarter", `${dateOf(state.week).label} begins.`);
  if (state.week >= state.settings.endWeek) {
    state.finished = true;
    state.finale = computeFinale(state);
    log(state, "finale", "The season is over. The 2035 awards are in: open the finale to see who won what.");
  }
}

export function runWeeks(state: GameState, n: number): void {
  for (let i = 0; i < n && !state.finished; i++) step(state);
}
